import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Plus, Trash } from "@phosphor-icons/react";

export const FILING_STATUSES = [
  { value: "single", label: "Single" },
  { value: "mfj", label: "Married Filing Jointly (MFJ)" },
  { value: "mfs", label: "Married Filing Separately (MFS)" },
  { value: "hoh", label: "Head of Household (HOH)" },
  { value: "qss", label: "Qualifying Surviving Spouse" },
];

export const INCOME_SOURCES = [
  { value: "w2", label: "W-2 Wages" },
  { value: "1099", label: "1099 Series (NEC, MISC, INT, DIV, B, R)" },
  { value: "k1", label: "Schedule K-1" },
  { value: "sched_c", label: "Schedule C (Business)" },
  { value: "rental", label: "Rental / Real Estate" },
  { value: "foreign", label: "Foreign Income" },
  { value: "ssa", label: "Social Security" },
  { value: "retirement", label: "Retirement / Pension" },
  { value: "unemployment", label: "Unemployment Benefits" },
];

export const DEDUCTIONS_CREDITS = [
  { value: "mortgage_interest", label: "Mortgage Interest" },
  { value: "property_taxes", label: "Property Taxes" },
  { value: "donations", label: "Charitable Donations" },
  { value: "medical", label: "Medical Expenses" },
  { value: "child_care", label: "Child / Dependent Care" },
  { value: "education", label: "Education Expenses" },
  { value: "student_loan", label: "Student Loan Interest" },
  { value: "hsa", label: "HSA Contributions" },
  { value: "ira", label: "IRA Contributions" },
  { value: "estimated_tax", label: "Estimated Tax Payments" },
];

export const DOCUMENT_CHECKLIST = [
  "Photo ID (Driver's License / Passport)",
  "SSN or ITIN letter",
  "W-2 forms",
  "1099 forms",
  "Prior year tax return",
  "Mortgage interest statement (1098)",
  "Property tax bill",
  "Charitable donation receipts",
  "Medical expense records",
  "K-1 statements",
  "Foreign bank statements",
  "Broker / crypto exchange statements",
];

export const newDependent = () => ({
  _key: (globalThis.crypto?.randomUUID?.() || `dep-${Date.now()}-${Math.random().toString(36).slice(2)}`),
  name: "", dob: "", ssn_or_itin: "", relationship: "", months_with_you: "",
});

export const withStableKey = (d) => (d && d._key ? d : { ...d, _key: (globalThis.crypto?.randomUUID?.() || `dep-${Date.now()}-${Math.random().toString(36).slice(2)}`) });

export const emptyBasicInfo = () => ({
  tax_year: new Date().getFullYear() - 1,
  is_submitted: false,
  full_name: "", ssn_or_itin: "", dob: "", occupation: "", phone: "", email: "",
  spouse_name: "", spouse_ssn_or_itin: "", spouse_dob: "", spouse_occupation: "",
  filing_status: "single",
  street: "", city: "", state: "", zip_code: "", country: "USA",
  dependents: [],
  income_sources: [], income_other: "",
  deductions_credits: [], deductions_other: "",
  bank_name: "", routing_number: "", account_number: "", account_type: "checking",
  filed_last_year: false, filing_required_prior_years: false, irs_notice_pending: false,
  foreign_bank_accounts: false, crypto_transactions: false, health_insurance: false,
  document_checklist: [],
  declaration_signature: "", declaration_date: "", notes: "",
});

function Section({ num, title, children, testId }) {
  return (
    <section className="rounded-md border tf-hairline bg-white overflow-hidden" data-testid={testId}>
      <header className="flex items-baseline gap-3 px-5 py-3 border-b tf-hairline bg-slate-50/60">
        <span className="font-mono text-xs uppercase tracking-widest text-[#D32F2F]">{String(num).padStart(2, "0")}</span>
        <h2 className="font-[Chivo] text-base font-black text-[#0A2540]">{title}</h2>
      </header>
      <div className="p-5 space-y-3">{children}</div>
    </section>
  );
}

function CheckRow({ id, checked, onChange, label, testId, disabled }) {
  return (
    <label htmlFor={id} className={`flex items-start gap-2.5 py-1 ${disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer"} group`}>
      <Checkbox id={id} checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={testId} className="mt-0.5" />
      <span className="text-sm text-slate-700 group-hover:text-[#0A2540]">{label}</span>
    </label>
  );
}

/**
 * Editable client-info form. Set `readOnly` to render every input as
 * a disabled control (used by the eye/view sheet). Set `disabled` to
 * lock the form after submission for clients (staff bypasses this).
 */
export default function BasicInfoForm({ form, setForm, readOnly = false, disabled = false }) {
  const locked = readOnly || disabled;
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (field, value) => {
    setForm((f) => {
      const list = new Set(f[field] || []);
      if (list.has(value)) list.delete(value); else list.add(value);
      return { ...f, [field]: Array.from(list) };
    });
  };

  const addDep = () => setField("dependents", [...(form.dependents || []), newDependent()]);
  const removeDep = (key) => setField("dependents", form.dependents.filter((d) => d._key !== key));
  const updDep = (key, field, value) => setField("dependents", form.dependents.map((d) => (d._key === key ? { ...d, [field]: value } : d)));

  return (
    <div className="space-y-4">
      <div className="rounded-md border tf-hairline bg-slate-50 p-4">
        <Label className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Tax Year</Label>
        <Input type="number" className="mt-1.5 w-32 text-lg font-[Chivo] font-black" value={form.tax_year} onChange={(e) => setField("tax_year", Number(e.target.value))} disabled={locked} data-testid="basic-tax-year" />
      </div>

      <Section num={1} title="Client Information" testId="section-client">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label>Full Name</Label><Input value={form.full_name} onChange={(e) => setField("full_name", e.target.value)} disabled={locked} data-testid="client-name" /></div>
          <div><Label>SSN or ITIN</Label><Input value={form.ssn_or_itin} onChange={(e) => setField("ssn_or_itin", e.target.value)} placeholder="XXX-XX-XXXX" disabled={locked} data-testid="client-ssn" /></div>
          <div><Label>Date of Birth</Label><Input type="date" value={form.dob} onChange={(e) => setField("dob", e.target.value)} disabled={locked} /></div>
          <div><Label>Occupation</Label><Input value={form.occupation} onChange={(e) => setField("occupation", e.target.value)} disabled={locked} /></div>
          <div><Label>Phone Number</Label><Input value={form.phone} onChange={(e) => setField("phone", e.target.value)} disabled={locked} /></div>
          <div><Label>Email Address</Label><Input type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} disabled={locked} /></div>
        </div>
      </Section>

      <Section num={2} title="Spouse Information (if applicable)" testId="section-spouse">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label>Spouse Name</Label><Input value={form.spouse_name} onChange={(e) => setField("spouse_name", e.target.value)} disabled={locked} /></div>
          <div><Label>Spouse SSN or ITIN</Label><Input value={form.spouse_ssn_or_itin} onChange={(e) => setField("spouse_ssn_or_itin", e.target.value)} disabled={locked} /></div>
          <div><Label>Spouse Date of Birth</Label><Input type="date" value={form.spouse_dob} onChange={(e) => setField("spouse_dob", e.target.value)} disabled={locked} /></div>
          <div><Label>Spouse Occupation</Label><Input value={form.spouse_occupation} onChange={(e) => setField("spouse_occupation", e.target.value)} disabled={locked} /></div>
        </div>
      </Section>

      <Section num={3} title="Filing Status" testId="section-filing-status">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
          {FILING_STATUSES.map((s) => (
            <label key={s.value} className={`flex items-center gap-2.5 py-1 ${locked ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}>
              <input type="radio" name="filing_status" checked={form.filing_status === s.value} onChange={() => setField("filing_status", s.value)} disabled={locked} className="accent-[#0A2540]" data-testid={`filing-${s.value}`} />
              <span className="text-sm text-slate-700">{s.label}</span>
            </label>
          ))}
        </div>
      </Section>

      <Section num={4} title="Address" testId="section-address">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2"><Label>Street</Label><Input value={form.street} onChange={(e) => setField("street", e.target.value)} disabled={locked} /></div>
          <div><Label>City</Label><Input value={form.city} onChange={(e) => setField("city", e.target.value)} disabled={locked} /></div>
          <div><Label>State</Label><Input value={form.state} onChange={(e) => setField("state", e.target.value)} disabled={locked} /></div>
          <div><Label>Zip Code</Label><Input value={form.zip_code} onChange={(e) => setField("zip_code", e.target.value)} disabled={locked} /></div>
          <div><Label>Country</Label><Input value={form.country} onChange={(e) => setField("country", e.target.value)} disabled={locked} /></div>
        </div>
      </Section>

      <Section num={5} title="Dependents" testId="section-dependents">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left p-2 border tf-hairline">Name</th>
                <th className="text-left p-2 border tf-hairline">DOB</th>
                <th className="text-left p-2 border tf-hairline">SSN / ITIN</th>
                <th className="text-left p-2 border tf-hairline">Relationship</th>
                <th className="text-left p-2 border tf-hairline">Months</th>
                {!locked && <th className="border tf-hairline w-8" />}
              </tr>
            </thead>
            <tbody>
              {(form.dependents || []).map((d, i) => (
                <tr key={d._key}>
                  <td className="p-1 border tf-hairline"><Input className="h-8 border-0" value={d.name} onChange={(e) => updDep(d._key, "name", e.target.value)} disabled={locked} data-testid={`dep-name-${i}`} /></td>
                  <td className="p-1 border tf-hairline"><Input type="date" className="h-8 border-0" value={d.dob} onChange={(e) => updDep(d._key, "dob", e.target.value)} disabled={locked} /></td>
                  <td className="p-1 border tf-hairline"><Input className="h-8 border-0" value={d.ssn_or_itin} onChange={(e) => updDep(d._key, "ssn_or_itin", e.target.value)} disabled={locked} /></td>
                  <td className="p-1 border tf-hairline"><Input className="h-8 border-0" value={d.relationship} onChange={(e) => updDep(d._key, "relationship", e.target.value)} disabled={locked} /></td>
                  <td className="p-1 border tf-hairline"><Input className="h-8 border-0" value={d.months_with_you} onChange={(e) => updDep(d._key, "months_with_you", e.target.value)} disabled={locked} /></td>
                  {!locked && (
                    <td className="p-1 border tf-hairline text-center">
                      <Button size="sm" variant="ghost" onClick={() => removeDep(d._key)} data-testid={`remove-dep-${i}`}><Trash size={13} className="text-[#D32F2F]" /></Button>
                    </td>
                  )}
                </tr>
              ))}
              {(!form.dependents || form.dependents.length === 0) && (
                <tr><td colSpan={locked ? 5 : 6} className="p-3 text-center text-xs text-slate-500 border tf-hairline">No dependents</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {!locked && <Button variant="outline" size="sm" onClick={addDep} data-testid="add-dependent"><Plus size={13} className="mr-1" /> Add dependent</Button>}
      </Section>

      <Section num={6} title="Income Sources" testId="section-income">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
          {INCOME_SOURCES.map((s) => (
            <CheckRow key={s.value} id={`inc-${s.value}`} checked={form.income_sources.includes(s.value)} onChange={() => toggle("income_sources", s.value)} label={s.label} testId={`inc-${s.value}`} disabled={locked} />
          ))}
        </div>
        <div className="mt-2"><Label>Other income (specify)</Label><Input value={form.income_other} onChange={(e) => setField("income_other", e.target.value)} disabled={locked} /></div>
      </Section>

      <Section num={7} title="Deductions & Credits" testId="section-deductions">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
          {DEDUCTIONS_CREDITS.map((s) => (
            <CheckRow key={s.value} id={`ded-${s.value}`} checked={form.deductions_credits.includes(s.value)} onChange={() => toggle("deductions_credits", s.value)} label={s.label} testId={`ded-${s.value}`} disabled={locked} />
          ))}
        </div>
        <div className="mt-2"><Label>Other (specify)</Label><Input value={form.deductions_other} onChange={(e) => setField("deductions_other", e.target.value)} disabled={locked} /></div>
      </Section>

      <Section num={8} title="Bank Information (Refund Direct Deposit)" testId="section-bank">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label>Bank Name</Label><Input value={form.bank_name} onChange={(e) => setField("bank_name", e.target.value)} disabled={locked} /></div>
          <div>
            <Label>Account Type</Label>
            <Select value={form.account_type} onValueChange={(v) => setField("account_type", v)} disabled={locked}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="checking">Checking</SelectItem><SelectItem value="savings">Savings</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>Routing Number</Label><Input value={form.routing_number} onChange={(e) => setField("routing_number", e.target.value)} maxLength={9} disabled={locked} /></div>
          <div><Label>Account Number</Label><Input value={form.account_number} onChange={(e) => setField("account_number", e.target.value)} disabled={locked} /></div>
        </div>
      </Section>

      <Section num={9} title="Additional Information" testId="section-additional">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
          <CheckRow id="a-filed" checked={form.filed_last_year} onChange={(v) => setField("filed_last_year", !!v)} label="Did you file a tax return last year?" disabled={locked} />
          <CheckRow id="a-prior" checked={form.filing_required_prior_years} onChange={(v) => setField("filing_required_prior_years", !!v)} label="Is filing required for previous years?" disabled={locked} />
          <CheckRow id="a-notice" checked={form.irs_notice_pending} onChange={(v) => setField("irs_notice_pending", !!v)} label="Have you received an IRS notice?" disabled={locked} />
          <CheckRow id="a-foreign" checked={form.foreign_bank_accounts} onChange={(v) => setField("foreign_bank_accounts", !!v)} label="Do you have foreign bank accounts?" disabled={locked} />
          <CheckRow id="a-crypto" checked={form.crypto_transactions} onChange={(v) => setField("crypto_transactions", !!v)} label="Did you have any crypto transactions?" disabled={locked} />
          <CheckRow id="a-health" checked={form.health_insurance} onChange={(v) => setField("health_insurance", !!v)} label="Did you have health insurance coverage?" disabled={locked} />
        </div>
      </Section>

      <Section num={10} title="Document Checklist" testId="section-checklist">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
          {DOCUMENT_CHECKLIST.map((d) => (
            <CheckRow key={d} id={`doc-${d}`} checked={form.document_checklist.includes(d)} onChange={() => toggle("document_checklist", d)} label={d} disabled={locked} />
          ))}
        </div>
      </Section>

      <Section num={11} title="Additional Notes" testId="section-notes">
        <Textarea rows={3} value={form.notes} onChange={(e) => setField("notes", e.target.value)} placeholder="Anything else your preparer should know…" disabled={locked} />
      </Section>

      <Section num={12} title="Client Declaration" testId="section-declaration">
        <p className="text-sm text-slate-700 leading-relaxed">
          I certify that the information I have provided here is true and complete to the best of my knowledge.
          I understand that Taxfree Filer LLP will rely on this information to prepare my tax return.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
          <div>
            <Label>Signature (type full name)</Label>
            <Input value={form.declaration_signature} onChange={(e) => setField("declaration_signature", e.target.value)} className="font-[cursive] italic text-lg" disabled={locked} data-testid="declaration-signature" />
          </div>
          <div>
            <Label>Date</Label>
            <Input type="date" value={form.declaration_date} onChange={(e) => setField("declaration_date", e.target.value)} disabled={locked} data-testid="declaration-date" />
          </div>
        </div>
      </Section>
    </div>
  );
}
